import { Component, Input, OnInit, ViewEncapsulation } from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { ConfigParameter } from "src/app/core/models/config-entities";
import { ConfigEntityService } from "../../../core/services/config-entity.service";
import { NavigationService } from "../../../core/services/navigation.service";
import { MatDialog } from "@angular/material/dialog";
import { SaveDeploymentSettingsComponent } from "src/app/modules/deployment/modals/save-deployment-settings/save-deployment-settings.component";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-config-section",
  templateUrl: "./config-section.component.html",
  styleUrls: ["./config-section.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ConfigSectionComponent implements OnInit {
  @Input() configEntities: ConfigParameter[] = [];
  private originalConfigEntities: ConfigParameter[] = [];
  sectionedConfigEntities: any = {};
  basicSectionedConfigEntities: any = {};
  advancedSectionedConfigEntities: any = {};
  activeEntity: any;
  activeChild: string = "";
  configForm: FormGroup = new FormGroup({});
  search: FormControl = new FormControl("");
  private originalAuthType: string = "";
  private destroy$ = new Subject<void>();
  private authTypeDialogShown: boolean = false; // Track if dialog was already shown
  private pendingAuthTypeValue: string = ""; // Store pending value

  constructor(
    private navService: NavigationService,
    private configEntityService: ConfigEntityService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Store the original complete list of parameters
    this.originalConfigEntities = JSON.parse(
      JSON.stringify(this.configEntities)
    );

    this.configEntities.forEach((param) => {
      if (!this.sectionedConfigEntities[param.parameterSection]) {
        this.sectionedConfigEntities[param.parameterSection] = [];
        this.basicSectionedConfigEntities[param.parameterSection] = [];
        this.advancedSectionedConfigEntities[param.parameterSection] = [];
      }

      const formControl = new FormControl(param.parameterValue, [
        Validators.pattern(param.parameterRegex),
      ]);

      this.configForm.addControl(param.parameterName, formControl);

      if (param.parameterDisabled) {
        this.configForm.get(param.parameterName).disable();
      }

      // Store original AuthType value and set up debounced change detection
      if (param.parameterName === "AuthType") {
        this.originalAuthType = param.parameterValue;

        // Subscribe to AuthType changes with debounce
        formControl.valueChanges
          .pipe(
            debounceTime(1000), // Wait 1 second after user stops typing
            distinctUntilChanged(), // Only trigger if value actually changed
            takeUntil(this.destroy$)
          )
          .subscribe((newValue) => {
            if (
              newValue !== this.originalAuthType &&
              newValue !== null &&
              newValue !== undefined &&
              newValue.toString().trim() !== ""
            ) {
              this.handleAuthTypeChange(newValue, formControl);
            }
          });
      }

      this.sectionedConfigEntities[param.parameterSection].push(param);

      if (param.parameterSettingType === "advanced") {
        this.advancedSectionedConfigEntities[param.parameterSection].push(
          param
        );
      } else {
        this.basicSectionedConfigEntities[param.parameterSection].push(param);
      }
    });
    this.handleConfigEntities();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private showAuthTypeChangeConfirmation(
    newValue: string,
    formControl: FormControl
  ): void {
    const authTypeNames = {
      "0": "SyncHPC API",
      "1": "Local AD",
      "2": "Azure AD",
      "3": "Okta",
      "4": "OpenId",
    };

    const newTypeName = authTypeNames[newValue] || `Type ${newValue}`;
    const originalTypeName =
      authTypeNames[this.originalAuthType] || `Type ${this.originalAuthType}`;
    const dialogRef = this.dialog.open(SaveDeploymentSettingsComponent, {
      disableClose: true,
      width: "550px",
      maxWidth: "90vw",
      data: {
        title: "Authentication Type Change",
        message: "",
        warningDialog: true,
        warningDialogContent: {
          originalAuthType: originalTypeName,
          newAuthType: newTypeName,
        },
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        // User confirmed the change
        this.authTypeDialogShown = true;
        this.pendingAuthTypeValue = newValue;
        this.originalAuthType = newValue;
      } else {
        // User cancelled, revert to original value
        formControl.setValue(this.originalAuthType, { emitEvent: false });
        // Reset the dialog state if user cancels
        this.authTypeDialogShown = false;
        this.pendingAuthTypeValue = "";
      }
    });
  }

  handleConfigEntities(): void {
    Object.keys(this.basicSectionedConfigEntities).forEach((key) => {
      if (this.basicSectionedConfigEntities[key].length === 0) {
        delete this.basicSectionedConfigEntities[key];
      }
    });

    Object.keys(this.advancedSectionedConfigEntities).forEach((key) => {
      if (this.advancedSectionedConfigEntities[key].length === 0) {
        delete this.advancedSectionedConfigEntities[key];
      }
    });
  }

  handleConfigEntitiesOperation(): void {
    this.basicSectionedConfigEntities = {};
    this.advancedSectionedConfigEntities = {};
    this.configEntities.forEach((param) => {
      if (!this.sectionedConfigEntities[param.parameterSection]) {
        this.sectionedConfigEntities[param.parameterSection] = [];
        this.basicSectionedConfigEntities[param.parameterSection] = [];
        this.advancedSectionedConfigEntities[param.parameterSection] = [];
      }

      // Only add control if it doesn't exist
      if (!this.configForm.get(param.parameterName)) {
        const formControl = new FormControl(param.parameterValue, [
          Validators.pattern(param.parameterRegex),
        ]);

        this.configForm.addControl(param.parameterName, formControl);

        // Re-subscribe to AuthType changes if this is AuthType parameter
        if (param.parameterName === "AuthType") {
          // Don't reset the original value here if dialog was already shown
          if (!this.authTypeDialogShown) {
            this.originalAuthType = param.parameterValue;
          }

          formControl.valueChanges
            .pipe(
              debounceTime(1000),
              distinctUntilChanged(),
              takeUntil(this.destroy$)
            )
            .subscribe((newValue) => {
              if (
                newValue !== this.originalAuthType &&
                newValue !== null &&
                newValue !== undefined &&
                newValue.toString().trim() !== ""
              ) {
                this.handleAuthTypeChange(newValue, formControl);
              }
            });
        }
      }

      if (param.parameterDisabled) {
        this.configForm.get(param.parameterName).disable();
      }
      this.sectionedConfigEntities[param.parameterSection].push(param);
      if (param.parameterSettingType === "advanced") {
        this.advancedSectionedConfigEntities[param.parameterSection].push(
          param
        );
      } else {
        this.basicSectionedConfigEntities[param.parameterSection].push(param);
      }
    });
    this.handleConfigEntities();
  }

  // Add method to reset dialog state if needed (e.g., when form is reset or reloaded)
  public resetAuthTypeDialogState(): void {
    this.authTypeDialogShown = false;
    this.pendingAuthTypeValue = "";
  }
  // ...existing code...
  getDisplayName(paramName: string): string {
    return this.configEntities.find((x) => x.parameterName == paramName)
      ?.parameterDisplayName;
  }

  getValues(): ConfigParameter[] | [] {
    let updatedValues: ConfigParameter[] = [];
    Object.keys(this.configForm.controls).forEach((key: any) => {
      if (this.configForm.controls[key].dirty) {
        // Always use the original complete list of parameters for lookup
        let param = this.originalConfigEntities.find(
          (x) => x.parameterName == key
        );

        // Only add to updatedValues if param is found
        if (param) {
          updatedValues.push({
            parameterName: key,
            parameterValue: this.configForm.controls[key].value,
            parameterSection: param.parameterSection,
            parameterDisplayName: param.parameterDisplayName,
            parameterDescription: param.parameterDescription,
            parameterTypeBoolean: param.parameterTypeBoolean,
            parameterDisabled: param.parameterDisabled,
            parameterSettingType: param.parameterSettingType,
          });
        }
      }
    });
    return updatedValues;
  }

  checkIfFormValid(): boolean {
    return this.configForm.valid;
  }

  getBasicSections() {
    return Object.entries(this.basicSectionedConfigEntities).sort(
      ([keyA], [keyB]) => keyA.localeCompare(keyB)
    );
  }

  getAdvancedSections() {
    return Object.entries(this.advancedSectionedConfigEntities).sort(
      ([keyA], [keyB]) => keyA.localeCompare(keyB)
    );
  }

  trackByParameter(index: number, item: any): string {
    return item.parameterName;
  }

  private handleAuthTypeChange(
    newValue: string,
    formControl: FormControl
  ): void {
    // If dialog was already shown once, don't show it again
    if (this.authTypeDialogShown) {
      return; // Allow the change without showing dialog again
    }

    // Show dialog only the first time AuthType is changed
    this.showAuthTypeChangeConfirmation(newValue, formControl);
  }
}
